import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, Send, X, Loader2, Layers, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function EntityChatDrawer({ entities }) {
  const [open, setOpen] = useState(false);
  const [entityId, setEntityId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || !entityId) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);
    try {
      const r = await api.post(`/entities/${entityId}/chat`, {
        entity_id: entityId, message: userMsg, session_id: sessionId || null,
      });
      setMessages(prev => [...prev, { role: 'assistant', text: r.data.response }]);
      if (r.data.session_id) setSessionId(r.data.session_id);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Error: ' + (err.response?.data?.detail || 'Failed to get response') }]);
    } finally { setLoading(false); }
  };

  const switchEntity = (eid) => {
    setEntityId(eid);
    setMessages([]);
    setSessionId('');
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="fixed bottom-6 right-6 w-12 h-12 bg-[#0A0A0A] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#171717] transition-colors z-50" data-testid="chat-fab">
        <Sparkles className="w-5 h-5" />
      </button>
    );
  }

  const selectedEntity = entities?.find(e => e.entity_id === entityId);

  return (
    <div className="fixed bottom-6 right-6 w-[420px] h-[560px] bg-white border border-[#E5E7EB] shadow-xl flex flex-col z-50" data-testid="entity-chat-drawer">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB] shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#002FA7]" />
          <span className="text-sm font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>Field Explorer</span>
        </div>
        <button onClick={() => setOpen(false)} className="text-[#9CA3AF] hover:text-[#4B5563]" data-testid="close-chat"><X className="w-4 h-4" /></button>
      </div>

      {/* Entity selector */}
      <div className="px-4 py-2 border-b border-[#E5E7EB] shrink-0">
        <Select value={entityId} onValueChange={switchEntity}>
          <SelectTrigger className="rounded-none h-8 text-xs" data-testid="chat-entity-select">
            <SelectValue placeholder="Select an entity to explore..." />
          </SelectTrigger>
          <SelectContent className="rounded-none z-[9999]">
            {(entities || []).map(e => (
              <SelectItem key={e.entity_id} value={e.entity_id} className="text-xs">
                <Layers className="w-3 h-3 inline mr-1.5" />{e.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedEntity && (
          <p className="text-[10px] text-[#9CA3AF] mt-1">{selectedEntity.attribute_count} attributes loaded as context</p>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-auto px-4 py-3 space-y-3" data-testid="chat-messages">
        {!entityId && (
          <div className="text-center py-8 text-sm text-[#9CA3AF]">
            <Layers className="w-8 h-8 mx-auto mb-2 text-[#E5E7EB]" />
            Select an entity to start exploring its fields
          </div>
        )}
        {entityId && messages.length === 0 && (
          <div className="text-center py-6 text-xs text-[#9CA3AF]">
            <p className="mb-2">Ask about <strong>{selectedEntity?.name}</strong> fields:</p>
            <div className="space-y-1">
              {['What fields are in this entity?', 'Which fields are coded vs free text?', 'Explain the clinical significance of each field'].map(q => (
                <button key={q} onClick={() => { setInput(q); }} className="block w-full text-left px-3 py-1.5 bg-[#F9FAFB] hover:bg-[#F3F4F6] text-[10px] text-[#4B5563] transition-colors">{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2 text-sm ${m.role === 'user' ? 'bg-[#0A0A0A] text-white' : 'bg-[#F9FAFB] text-[#4B5563] border border-[#E5E7EB]'}`}>
              {m.role === 'assistant' ? (
                <div className="prose prose-sm max-w-none prose-p:my-1 prose-li:my-0.5 text-xs">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
                </div>
              ) : (
                <span className="text-xs">{m.text}</span>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-3 py-2 bg-[#F9FAFB] border border-[#E5E7EB]">
              <Loader2 className="w-4 h-4 animate-spin text-[#9CA3AF]" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-[#E5E7EB] shrink-0">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={entityId ? "Ask about fields..." : "Select an entity first"}
            disabled={!entityId || loading}
            className="rounded-none text-xs min-h-[36px] max-h-[72px] resize-none flex-1"
            data-testid="chat-input"
            rows={1}
          />
          <Button onClick={handleSend} disabled={!entityId || !input.trim() || loading} className="rounded-none bg-[#0A0A0A] hover:bg-[#171717] self-end h-9 w-9 p-0" data-testid="chat-send">
            <Send className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
